import React, { useState, forwardRef, useEffect, useRef } from "react";
import Field from "@/pages/nueva-solicitud/components/Field.jsx";
import { InputFile } from "@/pages/nueva-solicitud/components/InputFile/InputFile.jsx";
import { InputComprobante } from "@/pages/nueva-solicitud/components/InputComprobante/InputComprobante.jsx";
import { Autocomplete, InputDate, InputMoney, InputDropdown, InputTextarea, Hint, Icon, Field as DesignSystemField } from "@artprov/design-system";
import {
  getConceptoGasto,
  getTipoComprobante,
  getOpcionesTipoCaeCai as getTipoAutorizacionAfip
} from "@/services";
import { extractDataFromComprobante } from "@/services/ia/ia.service.js";
import {
  conceptoGastoAdapter,
  tipoComprobanteAdapter,
  tiposCaeCaiAdapter
} from "@/services/adapters";
import { nuevaSolicitudSchema } from "../../../schemas/nuevaSolicitudSchema.js";
import { datosValidos } from "./nuevaSolicitud.utils.js";
import { useFormulario } from "../../../common/customHooks/useFormulario.js";
import InputCAECAICAE from "./InputCAECAICAE.jsx";
import { iaResponseAdapter } from "../../../services/adapters/iaResponse.adapter.js";
import { showIaAlert } from "../../../services/ia/ia.helper.js";
import { formatCuit } from "../../../utils/number.utils.js";

const datosAdapter = (datos) => {
  return {
    tipoComprobanteSelected: datos.idTipoComprobante,
    conceptoDelGastoSelected: datos.idConceptoDelGasto,
    tipoCaeCaiSelected: datos.tipoCaeCaiDesc,
    caeCai: datos.caeCai,
    datosDelComprobante: datos.numeroFactura,
    razonSocial: datos.razonSocial,
    numeroComprobante: datos?.numeroFactura?.split("-")[1],
    puntoVenta: datos?.numeroFactura?.split("-")[0],
    cuit: formatCuit(datos.cuit),
    fecha: datos.fecha,
    importe: datos.importe,
    archivoComprobante: datos.archivoComprobante,
    motivoGasto: datos.motivoGasto,
  }
}

export const NuevaSolicitudForm = forwardRef(
  (
    { datos, onCompletarComprobante, onSetFile, errorOnSubmit, onSetIaAlert },
    ref
  ) => {
    const { formData: formState, setFormData: setFormState, validateField, formErrors, setFormErrors, handleChangeFromValue, createErrorHint, clearErrorHint } = useFormulario(datos ? datosAdapter(datos) : {
      tipoComprobanteSelected: "",
      conceptoDelGastoSelected: "",
      tipoCaeCaiSelected: "",
      caeCai: "",
      datosDelComprobante: "",
      razonSocial: "",
      cuit: "",
      fecha: "",
      importe: "",
      archivoComprobante: "",
      motivoGasto: "",
    }, nuevaSolicitudSchema)

    const [isDisabled, setIsDisabled] = useState(true);
    const [disabledNumeroCae, setDisabledNumeroCae] = useState(false);
    const [disabledPorOtroComprobante, setDisabledPorOtroComprobante] = useState(false);
    const [clear, setClear] = useState(false);
    const [cuitValido, setCuitValido] = useState(false);
    const [isExtracting, setIsExtracting] = useState(false);

    //#region "FIELDS"
    const [opcionesTipoComprobante, setOpcionesTipoComprobante] = useState([]);
    const [opcionesConceptoDelGasto, setOpcionesConceptoDelGasto] = useState([]);
    const [opcionesTipoCaeCai, setOpcionesTipoCaeCai] = useState([]);
    const [tipoComprobanteSelected, setTipoComprobanteSelected] = useState(null);
    const [conceptoDelGastoSelected, setConceptoDelGastoSelected] = useState(null);
    const [tipoCaeCai, setTipoCaeCai] = useState();
    const [tipoCaeCaiDesc, setTipoCaeCaiDesc] = useState();
    const [sinCaeCai, setSinCaeCai] = useState(false);
    const [caeCai, setCaeCai] = useState();
    const [puntoVenta, setPuntoVenta] = useState();
    const [numeroFactura, setNumeroFactura] = useState();
    const [razonSocial, setRazonSocial] = useState();
    const [cuit, setCuit] = useState();
    const [fecha, setFecha] = useState();
    const [importe, setImporte] = useState();
    const [motivoGasto, setMotivoGasto] = useState();
    const [archivoComprobante, setArchivoComprobante] = useState();
    const [mostrarSinCae, setMostrarSinCae] = useState(false);
    const inputFile = useRef(null); //archivo comprobante
    const [opcionesTipoCae, setOpcionesTipoCae] = useState([]);
    //#endregion

    const getHintsForField = (fieldName, useVariant) => {
      const errorObject = formErrors.find((error) => error[fieldName]);
      return errorObject ? [{ variant: useVariant ? errorObject.variant : 'error', text: errorObject[fieldName], },] : [];
    };

    useEffect(() => {
      if (errorOnSubmit?.length > 0) {
        setFormErrors(
          (prev) =>
            [
              ...prev.filter((error) => !error["cuit"]),
              errorOnSubmit[0]
            ]
        )
      }
    }, [errorOnSubmit])

    useEffect(() => {
      const init = async () => {
        let tipoComprobante = await getTipoComprobante();
        const tipoComprobanteAdapted = tipoComprobanteAdapter(tipoComprobante);
        setOpcionesTipoComprobante(tipoComprobanteAdapted?.rows);

        let conceptoGasto = await getConceptoGasto();
        const conceptoGastoAdapted = conceptoGastoAdapter(conceptoGasto);
        setOpcionesConceptoDelGasto(conceptoGastoAdapted?.rows);

        let opcionesTipoAutorizacionAfip = await getTipoAutorizacionAfip()
        const opcionesTipoAutorizacionAfipAdapted = tiposCaeCaiAdapter(opcionesTipoAutorizacionAfip)
        setOpcionesTipoCaeCai(opcionesTipoAutorizacionAfipAdapted?.rows)

        if (datos) {
          cargarDatos(datos)
          onSetFile(datos.file)
          setArchivoComprobante(true)
          setMostrarSinCae(true)
        }
      };
      init();
      clearErrorHint()
    }, []);

    useEffect(() => {
      let opciones = [];
      for (const item of opcionesTipoCaeCai) {
        if (!item.sinCae || mostrarSinCae) {
          opciones.push(
            <InputDropdown.Option
              id={'cae' + item.id}
              value={item.nombre}
            >{item.nombre}</InputDropdown.Option>
          );
        }
      }
      setOpcionesTipoCae(opciones);
    }, [opcionesTipoCaeCai, mostrarSinCae, tipoComprobanteSelected]);

    const importeADecimal = (importe) => {
      return importe.replace(/\./g, "").replace(",", ".");
    }

    useEffect(() => {
      let comprobante = {};
      comprobante.esValido = datosValidos(comprobante, formState, nuevaSolicitudSchema, datos.idfiles3);
      if (tipoComprobanteSelected && conceptoDelGastoSelected) {
        comprobante.tipoCaeCai = tipoCaeCai;
        comprobante.tipoCaeCaiDesc = tipoCaeCaiDesc;
        comprobante.sinCaeCai = sinCaeCai;
        comprobante.caeCai = caeCai;
        comprobante.idTipoComprobante = tipoComprobanteSelected.id;
        comprobante.comprobante = tipoComprobanteSelected.descripcion;
        comprobante.concepto = conceptoDelGastoSelected.descripcion;
        comprobante.idConceptoDelGasto = conceptoDelGastoSelected.id;
        comprobante.puntoVenta = puntoVenta;
        comprobante.numero = numeroFactura;
        comprobante.numeroFactura = puntoVenta + '-' + numeroFactura;
        comprobante.razonSocial = razonSocial;
        comprobante.cuit = formatCuit(cuit);
        comprobante.fecha = fecha;
        comprobante.importeFormateado = '$' + importe;
        comprobante.importe = importeADecimal(importe);
        comprobante.archivoComprobante = inputFile;
        comprobante.motivoGasto = motivoGasto;
        comprobante.otroComprobante = (tipoComprobanteSelected.id == 71);
        comprobante.cuitValido = cuitValido;
        comprobante.esValido = datosValidos(comprobante, formState, nuevaSolicitudSchema, datos.idfiles3);

      }
      if (disabledNumeroCae) {
        setCaeCai('');
      }
      if (disabledPorOtroComprobante) {
        setCaeCai('');
        setPuntoVenta('');
        setNumeroFactura('');

        handleChangeFromValue('caeCai', "")
        handleChangeFromValue("tipoCaeCaiSelected", "")
        handleChangeFromValue("puntoVenta", "")
        handleChangeFromValue("numeroFactura", "")
      }
      onCompletarComprobante(comprobante);
    }, [
      tipoCaeCai,
      caeCai,
      tipoComprobanteSelected,
      conceptoDelGastoSelected,
      puntoVenta,
      numeroFactura,
      razonSocial,
      cuit,
      fecha,
      importe,
      archivoComprobante,
      motivoGasto,
      cuitValido,
      setArchivoComprobante
    ]);

    useEffect(() => {
      setIsDisabled(!(conceptoDelGastoSelected && archivoComprobante));

      if (tipoComprobanteSelected) {
        setMostrarSinCae(tipoComprobanteSelected.sinCae);
        setDisabledPorOtroComprobante(tipoComprobanteSelected.id == 71); //Otros comprobantes
      }

      if (disabledPorOtroComprobante) {
        setCaeCai('');
        setPuntoVenta('');
        setNumeroFactura('');
        setClear(true)
      } else {
        setClear(false)
      }

    }, [tipoComprobanteSelected, conceptoDelGastoSelected, archivoComprobante, handleChangeFromValue]);

    const handleFieldChange = (fieldName, value) => {
      setFormState((prev) => ({ ...prev, [fieldName]: value }));
      setFormErrors((prev) => prev.filter((error) => !error[fieldName]));
      validateField(fieldName, value);
    };

    const getStatusForField = (fieldName) => {
      const errorObject = formErrors.find((error) => error[fieldName]);
      return errorObject ? 'error' : 'default';
    };

    const cargarDatos = (datos) => {
      setTipoComprobanteSelected({ "id": datos.idTipoComprobante, "descripcion": datos.comprobante, "sinCae": datos.sinCaeCai })
      setConceptoDelGastoSelected({ "id": datos.idConceptoDelGasto, "descripcion": datos.concepto })
      setTipoCaeCai(datos.tipoCaeCai)
      setTipoCaeCaiDesc(datos.tipoCaeCaiDesc)
      setSinCaeCai(datos.sinCaeCai)
      setCaeCai(datos.caeCai)
      setPuntoVenta(datos.puntoVenta)
      setNumeroFactura(datos.numero)
      const formattedCuit = formatCuit(datos.cuit)
      setRazonSocial(datos.razonSocial)
      setCuit(formattedCuit)
      setFecha(datos.fecha)
      setImporte(datos.importe?.replace(".", ","))
      setMotivoGasto(datos.motivoGasto)
      setCuitValido(true)

      handleChangeFromValue("conceptoDelGastoSelected", datos.idConceptoDelGasto)
      handleChangeFromValue("tipoComprobanteSelected", datos.idTipoComprobante)
      handleChangeFromValue("cuit", formattedCuit)
    }

    const autoCompleteInputs = (data) => {
      if (!data) return;

      clearErrorHint();

      if (data.idTipoComprobante) {
        setTipoComprobanteSelected({ "id": data.idTipoComprobante, "descripcion": data.comprobante, "sinCae": data.sinCaeCai })
        handleChangeFromValue("tipoComprobanteSelected", data.idTipoComprobante)
      }
      const formattedCuit = formatCuit(data.cuit)
      setRazonSocial(data.razonSocial)
      setCuit(formattedCuit)
      setFecha(data.fecha)
      setImporte(data.importe?.replace(".", ","))
      setMotivoGasto(data.motivoGasto)
      handleFieldChange("razonSocial", data.razonSocial)
      handleFieldChange("cuit", formattedCuit)
      handleFieldChange("fecha", data.fecha)
      handleFieldChange("importe", data.importe?.replace(".", ","))
      handleFieldChange("motivoGasto", data.motivoGasto)

      if (data.idTipoComprobante && data.idTipoComprobante !== 71) {
        setTipoCaeCai(data.tipoCaeCai)
        setTipoCaeCaiDesc(data.tipoCaeCaiDesc)
        setCaeCai(data.caeCai)
        handleFieldChange("tipoCaeCaiSelected", data.tipoCaeCaiDesc)
        handleFieldChange("caeCai", data.caeCai)
        setDisabledPorOtroComprobante(false)
        setClear(false)
        setPuntoVenta(data.puntoVenta)
        setNumeroFactura(data.numero)
        handleFieldChange("puntoVenta", data.puntoVenta)
        handleFieldChange("numeroComprobante", data.numero)
      }
    }

    // Function to reset the input element #NE
    const handleResetInputFile = () => {
      if (inputFile.current) {
        inputFile.current.value = "";
        inputFile.current.type = "text";
        inputFile.current.type = "file";
      }

      setArchivoComprobante(false)
    };

    function validarExtension(archivo) {
      const extensionesPermitidas = ['pdf', 'jpg', 'png'];

      if (!archivo?.name) return false;

      const extension = archivo.name.split('.').pop().toLowerCase();
      return extensionesPermitidas.includes(extension);
    }

    const getRawFile = async (event) => {
      if (event?.target?.files?.length) {
        let file = event.target.files[0];
        let size = file.size;
        const maxMegaBytes = 5
        const maxSizeBytes = maxMegaBytes * 1024 * 1024;

        onSetIaAlert(null);
        clearErrorHint("comprobante")

        if (size > maxSizeBytes) {
          createErrorHint("comprobante", "El archivo no debe superar los 5MB")
          return handleResetInputFile()
        }

        if (!validarExtension(file)) {
          createErrorHint("comprobante", "Se puede subir en PDF, JPG o PNG.")
          return handleResetInputFile()
        }

        setIsExtracting(true);

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = async function () {
          setArchivoComprobante(true);
          const base64 = reader.result.replace(/^data:.+;base64,/, "");
          const formData = {
            user: "TESORERIA-FE",
            fileName: file.name,
            fileContent: base64,
            fileSize: size,
            contentType: file.type
          };
          onSetFile(formData);
        };

        try {
          const { respuesta: extractedData } = await extractDataFromComprobante(file);

          if (!extractedData || Object.keys(extractedData).length === 0) {
            showIaAlert('warning', onSetIaAlert);
          } else {
            const adaptedData = iaResponseAdapter(
              extractedData,
              opcionesTipoComprobante,
              opcionesTipoCaeCai,
              conceptoDelGastoSelected
            );

            autoCompleteInputs(adaptedData);

            showIaAlert('success', onSetIaAlert);
          }
        } catch {
          showIaAlert('warning', onSetIaAlert);
        } finally {
          setIsExtracting(false);
        }
      }
    }

    const opcionCAEaIdCae = (event) => {
      let idOpcion = 0
      opcionesTipoCaeCai.forEach((item, indice) => {
        if (item.nombre === event) {
          idOpcion = item.id
          setDisabledNumeroCae(item.sinCae)
          setSinCaeCai(item.sinCae)
          setTipoCaeCaiDesc(item.nombre)
        }
      });
      setTipoCaeCai(idOpcion)
    }

    return (
      <div className="modal-content bg-tertiary-100">
        {isExtracting && (
          <div className="fixed inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 z-50">
            <Icon variant="loading" classNameIcon="size-16 text-[64px]" />
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1 w-[526px]">
          <Autocomplete
            label="Concepto del gasto"
            name="conceptoDelGasto"
            id="conceptoDelGasto"
            searchValue={conceptoDelGastoSelected?.descripcion || ""}
            setSearchValue={(val) => {
              if (!val) {
                setConceptoDelGastoSelected(undefined);
                handleFieldChange("conceptoDelGastoSelected", undefined);
                validateField("conceptoDelGastoSelected");
              }
            }}
            placeholder="Seleccionar"
            opciones={opcionesConceptoDelGasto}
            classNameContainerInput="w-[257px]"
            classNameContainer="w-[257px]"
            autoComplete="off"
            setSelected={(selectedOption) => {
              if (selectedOption) {
                setConceptoDelGastoSelected(selectedOption);
                handleFieldChange("conceptoDelGastoSelected", selectedOption?.id);
              }
            }}
            hints={getHintsForField("conceptoDelGastoSelected")}
            onBlur={() => validateField("conceptoDelGastoSelected")}
            status={getStatusForField("conceptoDelGastoSelected")}
            onClear={() => {
              setConceptoDelGastoSelected(undefined)
              handleFieldChange("conceptoDelGastoSelected", undefined)
              validateField("conceptoDelGastoSelected")
            }}
          />
          <InputFile
            label="Adjuntar comprobante"
            placeholder="Adjuntar archivo"
            type="file"
            name="comprobante"
            id="comprobante"
            accept=".jpg, .png, .pdf"
            readOnly={true}
            rightFunction={() => handleResetInputFile()}
            onInput={(e) => getRawFile(e)}
            hints={getHintsForField("comprobante", true)}
            ref={inputFile}
            classNameContainer="w-[257px]"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1 w-[526px]">
          <Autocomplete
            label="Tipo de comprobante"
            name="tipoComprobante"
            id="tipoComprobante"
            searchValue={tipoComprobanteSelected?.descripcion || ""}
            setSearchValue={(val) => {
              if (!val) {
                setTipoComprobanteSelected(undefined);
                handleFieldChange("tipoComprobanteSelected", undefined);
                validateField("tipoComprobanteSelected");
              }
            }}
            placeholder="Seleccionar"
            classNameContainerInput="w-[257px]"
            classNameContainer="w-[257px]"
            autoComplete="off"
            disabled={isDisabled}
            opciones={opcionesTipoComprobante}
            setSelected={(selectedOption) => {
              if (selectedOption) {
                setTipoComprobanteSelected(selectedOption);
                handleFieldChange("tipoComprobanteSelected", selectedOption?.id);
                if (selectedOption.id == 71) { //Otros comprobantes
                  clearErrorHint("caeCai")
                }
              }
            }}
            hints={getHintsForField("tipoComprobanteSelected")}
            onBlur={() => validateField("tipoComprobanteSelected")}
            status={getStatusForField("tipoComprobanteSelected")}
            onClear={() => {
              setTipoComprobanteSelected(undefined)
              handleFieldChange("tipoComprobanteSelected", undefined)
              validateField("tipoComprobanteSelected")
            }}
          />
          <InputComprobante
            label="Datos del comprobante"
            name="datosDelComprobante"
            id="datosDelComprobante"
            disabled={isDisabled || disabledPorOtroComprobante}
            onChangePuntoVenta={(event) => {
              setPuntoVenta(event);
            }}
            onBlurPuntoVenta={(event) => handleFieldChange("puntoVenta", event)}
            onChangeNumero={(event) => {
              setNumeroFactura(event);
            }}
            onBlurNumero={(event) => handleFieldChange("numeroComprobante", event)}
            clear={clear}
            puntoVenta={puntoVenta}
            numeroFactura={numeroFactura}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1 w-[526px]">
          <InputDate
            label="Fecha"
            name="fecha"
            value={fecha}
            disabled={isDisabled}
            classNameContainer="w-[257px]"
            onChange={(event) => {
              setFecha(event);
              handleFieldChange("fecha", event);
            }}
            onBlur={() => validateField("fecha")}
            hints={getHintsForField("fecha")}
            status={getStatusForField("fecha")}
            autoComplete="off"
            calendarRenderMode="portal"
          />
          <DesignSystemField
            variant="cuit"
            label="CUIT"
            name="cuit"
            id="cuit"
            value={cuit}
            isDisabled={isDisabled}
            hints={getHintsForField("cuit")}
            autoVariantHints
            maxHints={1}
            onValidationChange={(result) => setCuitValido(result.status === "valid")}
            onChange={(newValue) => {
              setCuit(newValue);
              handleFieldChange("cuit", newValue);
            }}
            className="w-[257px]"
            classNames={{ root: "pt-2 gap-2" }}
            placeholder="Ingresá la CUIT del emisor"
          />
        </div>
        {formErrors[0]?.duplicidad && <Hint text={formErrors[0]?.duplicidad} variant="error" className="text-red-600" />}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1 w-[526px]">
          <Field
            label="Razón social"
            name="razonSocial"
            id="razonSocial"
            value={razonSocial}
            disabled={isDisabled}
            classNameContainerInput="w-[257px]"
            placeholder="Ingresá la razón social del emisor"
            onChange={(event) => {
              setRazonSocial(event.target.value);
              handleFieldChange("razonSocial", event.target.value);
            }}
            onBlur={() => validateField("razonSocial")}
            hints={getHintsForField("razonSocial")}
            status={getStatusForField("razonSocial")}
          />
          <InputMoney
            label="Importe"
            name="importe"
            id="importe"
            disabled={isDisabled}
            onChange={(event) => {
              setImporte(event);
              handleFieldChange("importe", event);
              // Validar número con hasta 7 enteros y opcional 2 decimales
              if (event.length > 9 && !event.includes(',')) {
                setFormErrors((prev) => [
                  ...prev.filter((error) => !error["importe"]),
                  { importe: 'El importe debe ser un número de hasta 7 posiciones enteras y 2 decimales, separados por coma. Ejemplo:  3.999.999,00' },
                ]);
              }
            }}
            onBlur={() => validateField("importe")}
            hints={getHintsForField("importe")}
            status={getStatusForField("importe")}
            value={importe}
            className="px-1"
            classNameContainer="w-[257px]"
            autoComplete="off"
            maxCharacters={12}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-[526px]">
          <InputCAECAICAE
            value={tipoCaeCai}
            formState={formState}
            disabled={isDisabled || disabledPorOtroComprobante}
            handleOnChange={opcionCAEaIdCae}
            clear={clear}
            formErrors={formErrors}
            opcionesTipoCae={opcionesTipoCae}
          />
          <Field
            label="Número de código de autorización"
            name="caeCai"
            id="caeCai"
            value={caeCai}
            disabled={isDisabled || disabledNumeroCae || disabledPorOtroComprobante}
            info="info"
            placeholder="Ingresá los números"
            onChange={(event) => {
              setCaeCai(event.target.value);
              handleFieldChange("caeCai", event.target.value);
              if (isDisabled || disabledNumeroCae || disabledPorOtroComprobante) {
                clearErrorHint("caeCai")
              }
            }}
            onBlur={() => validateField("caeCai")}
            hints={getHintsForField("caeCai")}
            status={getStatusForField("caeCai")}
            maxCharacters={14}
            className="w-[257px]"
            classNameContainerInput="w-[257px]"
            classNameContainer="w-[257px]"
          />
        </div>
        <div className="grid grid-cols-1">
          <InputTextarea
            label="Motivo del gasto"
            name="motivoGasto"
            id="motivoGasto"
            value={motivoGasto}
            placeholder="Agregá un comentario"
            maxLength={1000}
            disabled={isDisabled}
            className={{ ContainerInput: "w-[530px] h-[118px]" }}
            onChange={(event) => {
              setMotivoGasto(event.target.value);
              handleFieldChange("motivoGasto", event.target.value);
            }}
            onKeyUp={(event) => {
              if (event.key === "Backspace")
                if (event.target.value === "" && motivoGasto !== "") {
                  setMotivoGasto(event.target.value)
                  handleFieldChange("motivoGasto", event.target.value);
                }
            }}
            onBlur={() => { validateField("motivoGasto") }
            }
            hints={getHintsForField("motivoGasto")}
            status={getStatusForField("motivoGasto")}
          />
        </div>
      </div>
    );
  }
);
